import React from "react";

import { useGetVerificationEditingRequests } from "utils";

import { FieldVerification, PRACTICE_SLUG, VerificationEditingRequest } from "models";

import { Row, Tag } from "components";

import { PracticeList } from "pages/admin/verifications/components/lists/PracticeList";
import { CropTable } from "pages/admin/verifications/components/misc/CropTable";
import { CropTimeline } from "pages/admin/verifications/components/misc/CropTimeline";
import { EditingRequestInsightCards } from "pages/admin/verifications/components/misc/EditingRequestInsightCards";

export const CropList = ({ farmId }: { farmId: string }) => {
  const { data: editingRequests, isLoading: isLoadingEditingRequests } = useGetVerificationEditingRequests({ farmId, isAdmin: true });

  // Helper function to get editing requests for a specific field
  const getFieldEditingRequests = (fieldId: string): VerificationEditingRequest[] => editingRequests?.filter((request) => request.fieldId === fieldId) ?? [];

  // Helper function to get crop-level editing request status for a field
  const getCropEditingRequestStatus = (fieldId: string) => {
    const cropEditingRequests = editingRequests?.filter((request) => request.fieldId === fieldId && request.fieldCropId) ?? [];
    const missingCropRequests = editingRequests?.filter((request) => request.fieldId === fieldId && !request.fieldCropId) ?? [];
    const allEditingRequests = [...cropEditingRequests, ...missingCropRequests];

    if (allEditingRequests.length === 0) {
      return null;
    }

    const hasUncompletedRequests = allEditingRequests.some((req) => !req.completedAt);
    const hasUnreviewedRequests = allEditingRequests.some((req) => req.completedAt && !req.reviewedAt);
    const hasCompletedRequests = allEditingRequests.some((req) => req.completedAt && req.reviewedAt);

    if (hasUncompletedRequests) {
      return { text: "open request", colour: "red" as const };
    }

    if (hasUnreviewedRequests) {
      return { text: "review request", colour: "orange" as const };
    }

    if (hasCompletedRequests) {
      return { text: "completed request", colour: "blue" as const };
    }

    return null;
  };

  return (
    <>
      <Row className="padding-bottom-200">
        {/* prelint-ignore-col */}
        {/* Cols are inside component */}
        <EditingRequestInsightCards loading={isLoadingEditingRequests} editingRequests={editingRequests ?? []} requestType="crop" />
      </Row>

      <PracticeList
        farmId={farmId}
        practiceSlugs={[PRACTICE_SLUG.CROPS]}
        renderExtraColumns={(field: FieldVerification) => {
          const editingRequestStatus = getCropEditingRequestStatus(field.id);
          return editingRequestStatus ? <Tag colour={editingRequestStatus.colour} text={editingRequestStatus.text} /> : null;
        }}
        renderTimeline={(fieldId) => <CropTimeline fieldId={fieldId} />}
        renderExpandedContent={(field) => <CropTable field={field} farmId={farmId} editingRequests={getFieldEditingRequests(field.id)} />}
        additionalDrawerProps={{
          editingRequests: editingRequests ?? [],
        }}
      />
    </>
  );
};
